import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import HistoryIcon from "@mui/icons-material/History";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LiveDataRow } from "../models/LiveDataRow";
import { CheckItem } from "../utilities/checkItems";
import { buildChecklistExportRows, checklistFileName } from "../utilities/checklistExport";
import { buildChecklistGroups } from "../utilities/checklistGroups";
import { vehicleIdentification } from "../utilities/vehicleIdentification";
import { DeviceWithVehicleInfo } from "@/models/DeviceWithVehicleInfo";
import { createWorkbook, downloadBlob } from "@/utilities/xlsx";

interface Props {
  device?: DeviceWithVehicleInfo | null;
  /** Readings from the live window. */
  rows: LiveDataRow[];
  /** Readings from the last 24 hours, used where the live window has nothing. */
  fallbackRows?: LiveDataRow[];
}

/** A stable empty default, so leaving the history out does not rebuild the groups every render. */
const NO_ROWS: LiveDataRow[] = [];

const ICON_PROPS = { fontSize: "small", sx: { mt: 0.55, flexShrink: 0 } } as const;

/**
 * The checklist an installer works through before signing a vehicle off.
 *
 * Every line says whether the value was reported and what it was. A value that only turned up in
 * the last 24 hours is marked as such rather than shown as a plain tick: a vehicle parked
 * overnight reports nothing live, and "reported yesterday" is a different answer from
 * "reporting now" to someone verifying an installation.
 */
export function LiveDataChecklist({ device, rows, fallbackRows = NO_ROWS }: Props) {
  const { t, i18n } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(true);

  const groups = useMemo(
    () => buildChecklistGroups({ device, rows, fallbackRows, t, language: i18n.language }),
    [device, rows, fallbackRows, t, i18n.language],
  );

  const handleExportExcel = () => {
    const exportedAt = new Date();
    const workbook = createWorkbook(buildChecklistExportRows(device, groups, t));
    downloadBlob(workbook, checklistFileName(device, exportedAt));
  };

  return (
    <>
      <VehicleIdentification device={device} />
      <Paper variant="outlined" sx={{ mb: 1.5, borderRadius: 1, overflow: "hidden", flexShrink: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            px: 1.5,
            py: 1,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {t("liveData.checklist.title", "Installation and service checklist")}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              variant="outlined"
              onClick={handleExportExcel}
              startIcon={<DownloadOutlinedIcon fontSize="small" />}
            >
              {t("liveData.checklist.exportExcel", "Excel")}
            </Button>
            <IconButton
              size="small"
              onClick={() => setIsExpanded(previous => !previous)}
              aria-label={
                isExpanded
                  ? t("liveData.checklist.collapse", "Collapse checklist")
                  : t("liveData.checklist.expand", "Expand checklist")
              }
              sx={{ border: "1px solid", borderColor: "divider" }}
            >
              <ExpandMoreIcon
                sx={{
                  transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)",
                  transition: "transform 0.2s ease",
                }}
              />
            </IconButton>
          </Stack>
        </Box>
        {isExpanded && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "repeat(2, minmax(0, 1fr))",
                xl: "repeat(4, minmax(0, 1fr))",
              },
              gap: 1.5,
              px: 1.5,
              pb: 1.5,
              // Bounded so the checklist cannot push the reading grid off a short screen. It only
              // bites where the groups stack into one or two columns.
              maxHeight: "40vh",
              overflowY: "auto",
            }}
          >
            {groups.map(group => (
              <Box key={group.title} sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                  {group.title}
                </Typography>
                <Stack gap={0.75}>
                  {group.items.map(item => (
                    <ChecklistLine key={item.label} item={item} />
                  ))}
                </Stack>
              </Box>
            ))}
          </Box>
        )}
      </Paper>
    </>
  );
}

/** What the vehicle is, so the checklist below it can be read on its own. */
function VehicleIdentification({ device }: { device?: DeviceWithVehicleInfo | null }) {
  const { t } = useTranslation();
  const fields = vehicleIdentification(device, t, t("liveData.checklist.notFound", "not found"));

  return (
    <Paper variant="outlined" sx={{ mb: 1.5, borderRadius: 1, px: 1.5, py: 1, flexShrink: 0 }}>
      <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
        {fields.map(field => (
          <Box key={field.label}>
            <Typography variant="caption" color="text.secondary" component="div">
              {field.label}
            </Typography>
            <Typography variant="body2">{field.value}</Typography>
          </Box>
        ))}
      </Stack>
    </Paper>
  );
}

function ChecklistLine({ item }: { item: CheckItem }) {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
        alignItems: "center",
        gap: 0.75,
        width: "100%",
        minWidth: 0,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, minWidth: 0, pr: 1 }}>
        <StatusIcon item={item} />
        <Typography
          variant="body2"
          sx={{
            textAlign: "left",
            wordBreak: "break-word",
            color: item.found ? "text.primary" : "text.secondary",
          }}
        >
          {item.label}
        </Typography>
      </Box>
      <Typography
        variant="body2"
        sx={{
          textAlign: "right",
          wordBreak: "break-word",
          color: item.found ? "text.primary" : "text.secondary",
          pl: 1,
        }}
      >
        {item.value || t("liveData.checklist.notFound", "not found")}
      </Typography>
    </Box>
  );
}

function StatusIcon({ item }: { item: CheckItem }) {
  const { t } = useTranslation();

  if (!item.found) {
    return <HelpOutlineIcon color="disabled" {...ICON_PROPS} />;
  }

  if (item.fromFallback) {
    return (
      <Tooltip
        title={t(
          "liveData.checklist.fromHistoryHint",
          "Not reported live — this value is from the last 24 hours",
        )}
      >
        <HistoryIcon color="warning" {...ICON_PROPS} />
      </Tooltip>
    );
  }

  return <CheckCircleOutlineIcon color="success" {...ICON_PROPS} />;
}
